import { ref, watch, type Ref } from 'vue';
import { api } from '@/lib/http';

interface CacheEntry {
  at: number;
  promise: Promise<unknown[]>;
}

const cache = new Map<string, CacheEntry>();
const TTL_MS = 60_000;

/** Fetches dropdown options with a short shared cache, so several forms on one page make one request. */
export function fetchOptions<T>(url: string, params: Record<string, unknown> = {}): Promise<T[]> {
  const key = `${url}?${new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])).toString()}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise as Promise<T[]>;
  const promise = api.get<T[]>(url, { params }).catch((err) => {
    cache.delete(key);
    throw err;
  });
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

export function invalidateOptions(prefix: string) {
  for (const key of cache.keys()) if (key.startsWith(prefix)) cache.delete(key);
}

/** Reactive option list; reloads when `params` change. */
export function useOptions<T>(url: string, params?: Ref<Record<string, unknown>>) {
  const options = ref<T[]>([]) as Ref<T[]>;
  const loading = ref(false);
  async function load() {
    loading.value = true;
    try {
      options.value = await fetchOptions<T>(url, params?.value ?? {});
    } catch {
      options.value = [];
    } finally {
      loading.value = false;
    }
  }
  if (params) watch(params, load, { deep: true, immediate: true });
  else void load();
  return { options, loading, reload: load };
}

export interface UserOption {
  id: string;
  fullName: string;
  designation: string | null;
  divisions: { division: 'SOLAR' | 'TELECOM' }[];
}
